import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/landing.css';
import PublicNav from '../../components/public/PublicNav';
import PublicFooter from '../../components/public/PublicFooter';
import PricingSection from '../../components/public/PricingSection';
import FaqSection from '../../components/public/FaqSection';
import { getAdminToken } from '../../lib/api';
import { isAdmin } from '../../lib/roles';

const CAPABILITIES = [
  {
    title: 'Skill registry & governance',
    body: 'Versioned, risk-tiered skills — reviewed before they ever reach production.',
  },
  {
    title: 'Deterministic routing',
    body: 'Tasks land on the right agent from allowed skills, autonomy, and risk — fully auditable.',
  },
  {
    title: 'Approvals when it matters',
    body: 'Higher-risk runs pause for a human decision. Everything else moves.',
  },
  {
    title: 'Slack & Plane built in',
    body: 'Claude’s output posts to the thread and the work item your team already uses.',
  },
  {
    title: 'Credits & multi-tenant billing',
    body: 'Workspace pools and department suites so you can package AI work by customer.',
  },
  {
    title: 'Real RBAC',
    body: 'Admin, Operator, and Viewer — enforced on the API, not just hidden in the UI.',
  },
];

const STEPS = [
  {
    step: '01',
    title: 'Pick a skill',
    body: 'Choose from the governed catalog and paste a real request.',
  },
  {
    step: '02',
    title: 'Route & approve',
    body: 'We assign the agent. High-risk skills wait for approval.',
  },
  {
    step: '03',
    title: 'Claude runs',
    body: 'Structured output lands on Tasks — not a black-box chat.',
  },
  {
    step: '04',
    title: 'Slack + Plane',
    body: 'Thread and ticket update so the team can act immediately.',
  },
];

function trySkillTo(signedIn) {
  if (signedIn) return isAdmin() ? '/routing' : '/home';
  return { pathname: '/login', state: { from: { pathname: '/home' } } };
}

function ProductStage() {
  return (
    <div className="lp-stage" aria-hidden="true">
      <div className="lp-stage__frame">
        <div className="lp-stage__chrome">
          <span />
          <span />
          <span />
          <em>Routing Demo · Bug Triage</em>
        </div>
        <div className="lp-stage__body">
          <aside className="lp-stage__rail">
            <b>Skills</b>
            <i className="is-active">Bug Triage Assistant</i>
            <i>Social Post Pack</i>
            <i>CS Response Helper</i>
            <i>Onboarding Plan</i>
          </aside>
          <div className="lp-stage__main">
            <label>Request</label>
            <p>
              Checkout returns 500 after deploy. Started after the payments service
              rollout. Priority P1 — customers blocked.
            </p>
            <div className="lp-stage__meta">
              <span>Workspace · Acme</span>
              <span>Risk · Tier 1</span>
            </div>
            <div className="lp-stage__bar">
              <span className="lp-stage__run">Route → Claude</span>
              <span className="lp-stage__pulse" />
            </div>
            <div className="lp-stage__out">
              <strong>Output ready</strong>
              <small>Severity P1 · Likely cause: bad env on payments · Next: rollback + fix</small>
            </div>
            <div className="lp-stage__sync">
              <span>Slack · #all-kyma</span>
              <span>Plane · WS0001-18</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const signedIn = !!getAdminToken();
  const trySkill = trySkillTo(signedIn);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className={`lp-root ${ready ? 'lp-root--ready' : ''}`}>
      <PublicNav />

      <section className="lp-hero">
        <div className="lp-hero__glow" aria-hidden="true" />
        <div className="lp-hero__grid" aria-hidden="true" />
        <div className="lp-hero__shell">
          <div className="lp-hero__copy">
            <p className="lp-hero__brand">Enterprise Claude Skills</p>
            <h1 className="lp-hero__title">
              The control plane for Claude skills your team can trust
            </h1>
            <p className="lp-hero__sub">
              Route work to the right agent, approve when risk is high, and deliver results
              into Slack and Plane — governed end to end.
            </p>
            <div className="lp-hero__cta">
              <Link to={trySkill} className="lp-btn lp-btn--primary lp-btn--lg">
                Try a skill
              </Link>
              <Link to="/login" className="lp-btn lp-btn--ghost-light lp-btn--lg">
                Sign in
              </Link>
            </div>
          </div>
          <ProductStage />
        </div>
      </section>

      <section className="lp-how" id="how-it-works">
        <div className="lp-section__head lp-section__head--left">
          <p className="lp-eyebrow">How it works</p>
          <h2>Four steps from request to done</h2>
          <p className="lp-section__sub">
            The path operators follow in the product — and the story we demo live.
          </p>
        </div>
        <ol className="lp-how__list">
          {STEPS.map((s) => (
            <li key={s.step}>
              <span className="lp-how__num">{s.step}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="lp-features" id="features">
        <div className="lp-section__head lp-section__head--left">
          <p className="lp-eyebrow">Platform</p>
          <h2>Built for operators, not another chat window</h2>
          <p className="lp-section__sub">
            Catalog, route, approve, execute, and audit — one surface for every governed skill.
          </p>
        </div>
        <ul className="lp-capabilities">
          {CAPABILITIES.map((c) => (
            <li key={c.title}>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <PricingSection />

      <FaqSection />

      <section className="lp-cta">
        <div className="lp-cta__inner">
          <h2>Run a governed skill in minutes</h2>
          <p>
            Sign in, open Routing Demo, pick Bug Triage, and watch Claude → Slack → Plane
            complete the loop.
          </p>
          <div className="lp-hero__cta">
            <Link to={trySkill} className="lp-btn lp-btn--primary lp-btn--lg">
              Try a skill
            </Link>
            <a href="#pricing" className="lp-btn lp-btn--ghost-light lp-btn--lg">
              View pricing
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
